import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Plus, Copy, CheckCircle2, Clock, UserPlus, KeyRound, Pencil, Stethoscope, Users as UsersIcon, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/utils";
import { useOnline } from "@/lib/useOnline";
import { OfflineNotice } from "@/components/OfflineNotice";

const ROLE_LABEL = { handler: "Encargado", vet: "Veterinario" };

export default function UsersSettings() {
  const navigate = useNavigate();
  const online = useOnline();
  const [handlers, setHandlers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [farms, setFarms] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", farm_id: "", role: "handler" });
  const [inviteUrl, setInviteUrl] = useState("");
  const [cred, setCred] = useState({ name: "", username: "", password: "", farm_id: "", role: "handler" });
  const [createdCred, setCreatedCred] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", farm_id: "", password: "" });
  const [savingEdit, setSavingEdit] = useState(false);

  const load = async () => {
    if (!navigator.onLine) return;
    const [h, i, f] = await Promise.all([api.listHandlers(), api.listInvitations(), api.listFarms()]);
    setHandlers(h); setInvitations(i); setFarms(f);
    if (f[0]) {
      setForm((s) => ({ ...s, farm_id: s.farm_id || f[0].id }));
      setCred((s) => ({ ...s, farm_id: s.farm_id || f[0].id }));
    }
  };
  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!form.email) return toast.error("Email requerido");
    if (!form.farm_id) return toast.error("Finca requerida");
    try {
      const r = await api.inviteHandler(form);
      const url = `${window.location.origin}/invite/${r.token}`;
      setInviteUrl(url);
      toast.success("Invitación generada");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const copy = (txt) => { navigator.clipboard?.writeText(txt); toast.success("Copiado"); };

  const openEdit = (h) => {
    setEditTarget(h);
    setEditForm({ name: h.name || "", farm_id: (h.assigned_farm_ids || [])[0] || "", password: "" });
  };

  const saveEdit = async () => {
    if (!editForm.name) return toast.error("Nombre requerido");
    if (!editForm.farm_id) return toast.error("Finca requerida");
    if (editForm.password && editForm.password.length < 6) return toast.error("Contraseña de al menos 6 caracteres");
    setSavingEdit(true);
    try {
      const payload = { name: editForm.name, farm_id: editForm.farm_id };
      if (editForm.password) payload.password = editForm.password;
      await api.updateHandler(editTarget.id, payload);
      toast.success("Usuario actualizado");
      setEditTarget(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No pude actualizar"); }
    finally { setSavingEdit(false); }
  };

  // Fix P (2026-10-06): el dueño elimina un encargado o veterinario.
  const removeHandler = async (h) => {
    const who = `${ROLE_LABEL[h.role] || "usuario"} ${h.name}`;
    if (!window.confirm(`¿Eliminar a ${who}? Ya no va a poder entrar. Lo que cargó no se borra.`)) return;
    try {
      await api.deleteHandler(h.id);
      toast.success(`${who} eliminado`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No pude eliminar el usuario"); }
  };

  const toggleActive = async (h) => {
    try {
      await api.updateHandler(h.id, { is_active: !(h.is_active !== false) });
      toast.success(h.is_active !== false ? "Acceso suspendido" : "Acceso reactivado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No pude actualizar el acceso"); }
  };

  const createCred = async () => {
    if (!cred.name) return toast.error("Nombre requerido");
    if (!cred.username || cred.username.trim().length < 3) return toast.error("Usuario de al menos 3 caracteres");
    if (!cred.password || cred.password.length < 6) return toast.error("Contraseña de al menos 6 caracteres");
    if (!cred.farm_id) return toast.error("Finca requerida");
    try {
      await api.createHandler({ name: cred.name, username: cred.username.trim().toLowerCase(), password: cred.password, farm_id: cred.farm_id, role: cred.role });
      setCreatedCred({ username: cred.username.trim().toLowerCase(), password: cred.password });
      toast.success(cred.role === "vet" ? "Veterinario creado" : "Encargado creado");
      setCred((s) => ({ ...s, name: "", username: "", password: "" }));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" onClick={()=>navigate("/ajustes")} className="h-9 px-2"><ArrowLeft size={18} /></Button>
        <h1 className="font-heading text-2xl font-semibold flex-1" data-testid="users-page-title">Usuarios</h1>
        <Sheet open={open} onOpenChange={(o)=>{ setOpen(o); if (!o) { setInviteUrl(""); setCreatedCred(null); } }}>
          <SheetTrigger asChild><Button className="h-9" disabled={!online} data-testid="users-invite-button"><Plus size={14} className="mr-1"/>Agregar</Button></SheetTrigger>
          <SheetContent side="bottom" className="rounded-t-2xl max-h-[90vh] overflow-y-auto">
            <SheetHeader><SheetTitle>Agregar usuario</SheetTitle></SheetHeader>
            <Tabs defaultValue="cred" className="py-3">
              <TabsList className="w-full grid grid-cols-2">
                <TabsTrigger value="cred" data-testid="users-tab-cred"><KeyRound size={14} className="mr-1.5" />Usuario y clave</TabsTrigger>
                <TabsTrigger value="invite" data-testid="users-tab-invite"><UserPlus size={14} className="mr-1.5" />Invitar por link</TabsTrigger>
              </TabsList>

              {/* Alta directa con usuario + contraseña */}
              <TabsContent value="cred" className="space-y-3 mt-3">
                <p className="text-xs text-muted-foreground">Creá la cuenta y entregale el usuario y la contraseña. Inicia sesión sin email.</p>
                <div>
                  <Label>Rol</Label>
                  <Select value={cred.role} onValueChange={(v)=>setCred({ ...cred, role: v })}>
                    <SelectTrigger className="h-11" data-testid="users-cred-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="handler">Encargado</SelectItem>
                      <SelectItem value="vet">Veterinario</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Nombre</Label><Input data-testid="users-cred-name" value={cred.name} onChange={(e)=>setCred({ ...cred, name: e.target.value })} className="h-11" /></div>
                <div><Label>Usuario</Label><Input data-testid="users-cred-username" value={cred.username} onChange={(e)=>setCred({ ...cred, username: e.target.value.replace(/\s/g, "") })} className="h-11" placeholder="juanperez" autoCapitalize="none" /></div>
                <div><Label>Contraseña</Label><Input data-testid="users-cred-password" value={cred.password} onChange={(e)=>setCred({ ...cred, password: e.target.value })} className="h-11" placeholder="mínimo 6 caracteres" /></div>
                <div>
                  <Label>Finca asignada</Label>
                  <Select value={cred.farm_id} onValueChange={(v)=>setCred({ ...cred, farm_id: v })}>
                    <SelectTrigger className="h-11" data-testid="users-cred-farm"><SelectValue /></SelectTrigger>
                    <SelectContent>{farms.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Button onClick={createCred} className="h-12 w-full" data-testid="users-cred-submit">{cred.role === "vet" ? "Crear veterinario" : "Crear encargado"}</Button>
                {createdCred && (
                  <div className="bg-accent/40 border border-border rounded-xl p-3 text-sm" data-testid="users-cred-result">
                    <div className="font-medium mb-1.5 flex items-center gap-1.5"><CheckCircle2 size={15} className="text-[hsl(var(--state-present))]" />Credenciales del usuario</div>
                    <div className="font-mono text-[13px]">Usuario: {createdCred.username}</div>
                    <div className="font-mono text-[13px]">Contraseña: {createdCred.password}</div>
                    <Button size="sm" variant="outline" className="mt-2 h-8" onClick={()=>copy(`Usuario: ${createdCred.username}\nContraseña: ${createdCred.password}`)}><Copy size={13} className="mr-1" />Copiar</Button>
                  </div>
                )}
              </TabsContent>

              {/* Invitación por link */}
              <TabsContent value="invite" className="space-y-3 mt-3">
                <div>
                  <Label>Rol</Label>
                  <Select value={form.role} onValueChange={(v)=>setForm({ ...form, role: v })}>
                    <SelectTrigger className="h-11" data-testid="users-invite-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="handler">Encargado</SelectItem>
                      <SelectItem value="vet">Veterinario</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Nombre</Label><Input data-testid="users-invite-name" value={form.name} onChange={(e)=>setForm({ ...form, name: e.target.value })} className="h-11" /></div>
                <div><Label>Email</Label><Input data-testid="users-invite-email" type="email" value={form.email} onChange={(e)=>setForm({ ...form, email: e.target.value })} className="h-11" /></div>
                <div>
                  <Label>Finca asignada</Label>
                  <Select value={form.farm_id} onValueChange={(v)=>setForm({ ...form, farm_id: v })}>
                    <SelectTrigger className="h-11" data-testid="users-invite-farm"><SelectValue /></SelectTrigger>
                    <SelectContent>{farms.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Button onClick={invite} className="h-12 w-full" data-testid="users-invite-submit">Generar invitación</Button>
                {inviteUrl && (
                  <div className="bg-accent/40 border border-border rounded-xl p-3 text-xs">
                    <div className="font-medium mb-1">Compartí este link:</div>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 truncate font-mono text-[11px]">{inviteUrl}</code>
                      <Button size="icon" variant="outline" onClick={()=>copy(inviteUrl)}><Copy size={14} /></Button>
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </SheetContent>
        </Sheet>
      </div>

      {!online && <OfflineNotice label="La gestión de usuarios requiere conexión." />}

      <Card>
        <CardHeader><CardTitle className="text-base">Usuarios</CardTitle></CardHeader>
        <CardContent className="p-0 divide-y divide-border">
          {handlers.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">Todavía no agregaste a nadie.</p>}
          {handlers.map((h) => {
            const active = h.is_active !== false;
            const isVetRole = h.role === "vet";
            return (
              <div key={h.id} className="p-3 flex items-center gap-2 text-sm" data-testid={`users-row-${h.id}`}>
                <div className={`w-9 h-9 rounded-full grid place-items-center ${isVetRole ? "bg-sky-50 text-sky-800" : "bg-emerald-50 text-emerald-800"}`}>
                  {isVetRole ? <Stethoscope size={16} /> : <UsersIcon size={16} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium flex items-center gap-1.5 flex-wrap">
                    {h.name}
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0" data-testid={`users-role-${h.id}`}>{ROLE_LABEL[h.role] || h.role}</Badge>
                    {!active && <Badge variant="destructive" className="text-[10px] px-1.5 py-0">Suspendido</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground">{h.username ? `@${h.username}` : h.email} · {(h.assigned_farm_ids || []).length} finca(s)</div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Switch checked={active} onCheckedChange={()=>toggleActive(h)} data-testid={`users-toggle-active-${h.id}`} aria-label="Acceso activo" />
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={()=>openEdit(h)} data-testid={`users-edit-handler-${h.id}`}>
                    <Pencil size={14} />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" disabled={!online} onClick={()=>removeHandler(h)} data-testid={`users-delete-handler-${h.id}`} aria-label="Eliminar usuario">
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Sheet open={!!editTarget} onOpenChange={(o)=>{ if (!o) setEditTarget(null); }}>
        <SheetContent side="bottom" className="rounded-t-2xl max-h-[90vh] overflow-y-auto">
          <SheetHeader><SheetTitle>Editar usuario</SheetTitle></SheetHeader>
          <div className="py-3 space-y-3">
            <div><Label>Nombre</Label><Input data-testid="users-edit-name" value={editForm.name} onChange={(e)=>setEditForm({ ...editForm, name: e.target.value })} className="h-11" /></div>
            <div>
              <Label>Finca asignada</Label>
              <Select value={editForm.farm_id} onValueChange={(v)=>setEditForm({ ...editForm, farm_id: v })}>
                <SelectTrigger className="h-11" data-testid="users-edit-farm"><SelectValue /></SelectTrigger>
                <SelectContent>{farms.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nueva contraseña (opcional)</Label>
              <Input data-testid="users-edit-password" value={editForm.password} onChange={(e)=>setEditForm({ ...editForm, password: e.target.value })} className="h-11" placeholder="Dejar en blanco para no cambiarla" />
            </div>
            <Button onClick={saveEdit} disabled={savingEdit} className="h-12 w-full" data-testid="users-edit-submit">{savingEdit ? "Guardando…" : "Guardar cambios"}</Button>
          </div>
        </SheetContent>
      </Sheet>

      <Card>
        <CardHeader><CardTitle className="text-base">Invitaciones</CardTitle></CardHeader>
        <CardContent className="p-0 divide-y divide-border">
          {invitations.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">Sin invitaciones.</p>}
          {invitations.map((i) => (
            <div key={i.id} className="p-3 text-sm flex items-center gap-2">
              <Clock size={14} className="text-muted-foreground" />
              <div className="flex-1">
                <div className="font-medium flex items-center gap-1.5">{i.name} <Badge variant="outline" className="text-[10px] px-1.5 py-0">{ROLE_LABEL[i.role] || "Encargado"}</Badge></div>
                <div className="text-xs text-muted-foreground">{i.email} · {i.used_at ? `Activada ${formatDateTime(i.used_at)}` : `Pendiente desde ${formatDateTime(i.created_at)}`}</div>
              </div>
              {!i.used_at && (
                <Button variant="outline" size="sm" onClick={()=>{ navigator.clipboard?.writeText(`${window.location.origin}/invite/${i.token}`); toast.success("Link copiado"); }}><Copy size={14} /></Button>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
